/**
 * The three platform roles. Mirrors `Role` in the API (services/api/app/domains/users).
 * "Volunteer" and "member" are Employee-type accounts distinguished by
 * `memberType`, not by role, so permission logic stays three-way.
 */
export const ROLES = ["super_admin", "smm", "employee"] as const;
export type Role = (typeof ROLES)[number];

/** Which portal a role lands in. Route prefixes are stable public URLs. */
export const PORTALS = {
  super_admin: { key: "admin", path: "/admin", label: "The Motherhood" },
  smm: { key: "org", path: "/org", label: "Org Admin" },
  employee: { key: "me", path: "/me", label: "My Space" },
} as const satisfies Record<Role, { key: string; path: `/${string}`; label: string }>;

export type PortalKey = (typeof PORTALS)[Role]["key"];

export function homePathFor(role: Role): string {
  return PORTALS[role].path;
}

/** The portals a role may be sent back into after signing in. */
const ADMITTED: Record<Role, readonly string[]> = {
  super_admin: [PORTALS.super_admin.path],
  // The SMM posts like everyone else (SB-40), so their own month is a fair
  // place to land as well as their org.
  smm: [PORTALS.smm.path, PORTALS.employee.path],
  employee: [PORTALS.employee.path],
};

/**
 * Where to land after signing in: the page they were sent away from, when it
 * belongs to their portal, else home. The redirect on /login is written for
 * whoever was signed in before (an expired employee session leaves
 * `?redirect=/me`), and the next person to sign in may be a Super Admin, who
 * would otherwise land in the employee portal with its nav and a page they
 * cannot load.
 */
export function landingFor(role: Role, redirect: string | undefined): string {
  if (!redirect || !redirect.startsWith("/") || redirect.startsWith("//")) {
    return homePathFor(role);
  }
  const path = redirect.split(/[?#]/, 1)[0] ?? redirect;
  const admitted = ADMITTED[role].some((p) => path === p || path.startsWith(`${p}/`));
  return admitted ? redirect : homePathFor(role);
}

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}
